import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, getToken, qs } from '../api/client';
import { urlImpression } from '../api/config';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Champ, Modale, Squelette } from '../components/ui';
import { FormulaireClient } from '../components/FormulaireClient';
import { ajouteJours, centimes, dateFr, deviseReference, isoJour, montant, nomClient } from '../lib/format';
import { T } from '../i18n';
import type { Blocage, Client, Vehicule, Verdict } from '../api/types';

interface Dispo { disponibles: { vehicle: Vehicule; totalMinor: number; tarifJourMinor: number; saison: string }[] }

/**
 * L'assistant en trois etapes. L'etape 2 est celle qui compte :
 * l'article 01 est calcule en direct, il bloque, et seul un gerant peut deroger
 * — en laissant son nom au journal.
 */
interface SuppDepart {
  _id: string; code: string; libelle: string; unite: string; prixMinor: number;
  moment: string; actif: boolean;
}

/** L'heure d'une location quand personne ne la precise : le matin, a l'ouverture. */
const HEURE_DEFAUT = '09:00';

export default function NouvelleReservation() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { peut } = useAuth();

  const [etape, setEtape] = useState(1);
  const [debut, setDebut] = useState(params.get('debut') ?? isoJour(new Date()));
  const [fin, setFin] = useState(params.get('fin') ?? isoJour(ajouteJours(new Date(), 7)));
  /*
   * Les heures sont facultatives.
   *
   * La plupart des locations partent et rentrent le matin : imposer deux
   * horloges a chaque saisie ferait perdre du temps a l'agent pour rien. Quand
   * elles servent — une voiture donnee a 14 h, une location dans la journee —
   * on les precise, et tout le reste suit : la recherche de disponibilite, le
   * tampon de preparation, le planning et le calcul du retard.
   */
  const [avecHeures, setAvecHeures] = useState(false);
  /*
   * Facturer au JOUR ou au MOIS.
   *
   * Au mois, on ne demande pas une date de retour mais un NOMBRE DE MOIS : la
   * date se déduit, du quantième au même quantième. C'est ce qui fait
   * disparaître le problème des 28/31 jours — il n'y a plus rien à comparer.
   */
  const [auMois, setAuMois] = useState(false);
  const [nbMois, setNbMois] = useState(1);
  const [tarifMois, setTarifMois] = useState<number | ''>('');
  const [heureDebut, setHeureDebut] = useState(HEURE_DEFAUT);
  const [heureFin, setHeureFin] = useState(HEURE_DEFAUT);
  const [horaires, setHoraires] = useState<{ ouverture: string; fermeture: string } | null>(null);
  /*
   * LA TVA SE DECIDE ICI, UNE FOIS POUR TOUTES.
   *
   * Cochee, la location est vendue TTC : le total devient HT x 1,19, et la
   * facture reprend ce choix. Avant, la facture le devinait du type de client :
   * une location vendue 75 000 a une societe sortait facturee 89 250 — deux
   * papiers, deux montants, sur le meme dossier.
   */
  const [avecTva, setAvecTva] = useState(false);
  const [tauxTva, setTauxTva] = useState(19);
  /* Le nom de la taxe (TVA, VAT, GST…) et la case cochee d'office viennent des Parametres. */
  const [nomTaxe, setNomTaxe] = useState<string | null>(null);
  const [taxeParDefaut, setTaxeParDefaut] = useState(false);
  useEffect(() => {
    api.get<{ heureOuverture: string; heureFermeture: string; tauxTvaPct?: number; nomTaxe?: string; taxeParDefaut?: boolean }>('/parametres')
      .then((p) => {
        setHoraires({ ouverture: p.heureOuverture, fermeture: p.heureFermeture });
        if (typeof p.tauxTvaPct === 'number') setTauxTva(p.tauxTvaPct);
        if (typeof p.nomTaxe === 'string' && p.nomTaxe.trim()) setNomTaxe(p.nomTaxe.trim());
        if (p.taxeParDefaut) { setTaxeParDefaut(true); setAvecTva(true); }
      })
      .catch(() => {});
  }, []);

  const hDebut = avecHeures ? heureDebut : HEURE_DEFAUT;
  const hFin = avecHeures ? heureFin : HEURE_DEFAUT;
  const momentDebut = `${debut}T${hDebut}:00`;
  /** Ajoute des mois calendaires en écrêtant les fins de mois — 31 janv. + 1 mois = 28 févr. */
  const ajouterMois = (iso: string, n: number) => {
    const [an, m, j] = iso.split('-').map(Number);
    const dernier = new Date(an!, m! - 1 + n + 1, 0).getDate();
    const d = new Date(an!, m! - 1 + n, Math.min(j!, dernier));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const finEffective = auMois ? ajouterMois(debut, nbMois) : fin;
  const momentFin = `${finEffective}T${hFin}:00`;
  const [dispo, setDispo] = useState<Dispo | null>(null);
  const [vehiculeId, setVehiculeId] = useState(params.get('vehicle') ?? '');

  const [recherche, setRecherche] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [client, setClient] = useState<Client | null>(null);
  // Une societe demande presque toujours la facture avec TVA : on propose la case cochee.
  useEffect(() => { if (client) setAvecTva(taxeParDefaut || client.type === 'societe'); }, [client, taxeParDefaut]);

  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [derogations, setDerogations] = useState<Record<string, string>>({});
  const [demandeDerog, setDemandeDerog] = useState<Blocage | null>(null);
  const [nouveauClient, setNouveauClient] = useState(false);
  const [motifSaisi, setMotifSaisi] = useState('');

  const [tarifNegocie, setTarifNegocie] = useState<number | ''>('');
  const [kmIllimite, setKmIllimite] = useState(false);
  const [remise, setRemise] = useState<number | ''>('');
  const [motifRemise, setMotifRemise] = useState('');
  /* Payee au comptoir par defaut : le credit doit etre un choix, jamais un oubli. */
  const [reglement, setReglement] = useState<'sur_place' | 'differe' | 'par_tranche'>('sur_place');
  const [tranche, setTranche] = useState<number | ''>('');
  const [trancheMode, setTrancheMode] = useState('especes');
  /*
   * LE CALENDRIER DES VERSEMENTS.
   *
   * « Paiement par tranches avec la possibilité de faire un planning des dates
   * de paiement, on affiche ça dans le contrat. » Sans dates écrites, le reste
   * à payer n'est qu'un chiffre : personne ne sait quand relancer, et le client
   * n'a rien signé sur les échéances.
   */
  const [echeances, setEcheances] = useState<{ date: string; montant: string }[]>([]);
  /* Le compteur au moment de la remise des cles. Il ne remplace pas le releve
   * officiel — celui-la a ses fenetres — il repond a une seule question :
   * cette voiture peut-elle sortir, ou faut-il la vidanger d'abord ? */
  const [kmActuel, setKmActuel] = useState('');
  /* La caution convenue au comptoir. Elle ne s'encaisse pas ici — la
   * decharge a besoin du contrat — mais elle est notee, pour que personne
   * n'ait a s'en souvenir au moment d'etablir le dossier. */
  const [caution, setCaution] = useState('');
  const [cautionForme, setCautionForme] = useState<'especes' | 'cheque' | 'traite' | 'carte'>('especes');
  const [cautionPiece, setCautionPiece] = useState('');
  /* Prise au comptoir tout de suite, ou seulement convenue ? La decharge a
   * besoin d'un contrat : si on l'encaisse maintenant, le contrat s'etablit
   * dans la foulee et la decharge part a l'impression. */
  const [cautionMaintenant, setCautionMaintenant] = useState(true);
  const [vidanges, setVidanges] = useState<{ vehicleId: string; prochainKm?: number | null; km: number }[]>([]);
  useEffect(() => {
    api.get<{ lignes: { vehicleId: string; prochainKm?: number | null; km: number }[] }>('/operations/echeances/vidange')
      .then((r) => setVidanges(r.lignes)).catch(() => {});
  }, []);
  const [erreur, setErreur] = useState<ApiError | null>(null);
  const [verdictKo, setVerdictKo] = useState(false);
  // « Un seul catalogue de supplements — le siege bebe et le retard dans la meme
  // liste » : la moitie « retour » etait cablee, la moitie « depart » n'existait
  // pas. Le serveur acceptait deja `supplements`, aucun ecran ne les proposait.
  const [catalogue, setCatalogue] = useState<SuppDepart[]>([]);
  const [gerantPrevenu, setGerantPrevenu] = useState(false);
  const [envoiPrevenir, setEnvoiPrevenir] = useState(false);
  const [quantites, setQuantites] = useState<Record<string, number>>({});
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    // Les heures comptent ici aussi : une voiture qui rentre a 11 h n'est pas
    // libre a 11 h, et elle l'est a 14 h. Une location dans la journee devient
    // possible des que les heures different.
    if (momentFin > momentDebut) {
      api.get<Dispo>(`/disponibilites${qs({ debut: momentDebut, fin: momentFin })}`).then(setDispo);
    }
  }, [momentDebut, momentFin]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get<Client[]>(`/clients${qs({ q: recherche })}`).then(setClients).catch(() => {});
    }, 220);
    return () => clearTimeout(t);
  }, [recherche]);

  useEffect(() => {
    if (!client) { setVerdict(null); setVerdictKo(false); return; }
    setVerdictKo(false);
    api.post<Verdict>('/clients/verifier', {
      dateNaissance: client.dateNaissance, permisDelivreLe: client.permisDelivreLe,
      listeNoire: client.listeNoire, motifListeNoire: client.motifListeNoire,
    }).then((v) => { setVerdict(v); setVerdictKo(false); })
      // Si la verification tombe, on NE PASSE PAS. Une liste de blocages vide
      // parce que l'appel a echoue est indiscernable d'un client conforme :
      // c'est precisement comme ca que l'article 01 se neutralise en silence.
      .catch(() => { setVerdict(null); setVerdictKo(true); });
  }, [client]);

  useEffect(() => {
    api.get<SuppDepart[]>('/supplements')
      .then((l) => setCatalogue(l.filter((x) => x.actif && x.moment === 'depart')))
      .catch(() => setCatalogue([]));
  }, []);

  const choisi = useMemo(() => dispo?.disponibles.find((x) => x.vehicle._id === vehiculeId), [dispo, vehiculeId]);
  // Le meme calcul que le serveur, heures comprises : une location de 09 h a
  // 17 h le meme jour fait un jour, pas zero.
  const jours = Math.max(1, Math.ceil((+new Date(momentFin) - +new Date(momentDebut)) / 86400000));
  const tarifRef = choisi?.tarifJourMinor ?? 0;
  const tarifApplique = tarifNegocie === '' ? tarifRef : centimes(Number(tarifNegocie));
  const ecartPct = tarifRef ? Math.round(((tarifApplique - tarifRef) / tarifRef) * 1000) / 10 : 0;
  const lignesSupp = catalogue
    .filter((x) => (quantites[x.code] ?? 0) > 0)
    .map((x) => ({ ...x, quantite: quantites[x.code]!, totalMinor: x.prixMinor * quantites[x.code]! }));
  const totalSupp = lignesSupp.reduce((a, l) => a + l.totalMinor, 0);
  /* Au mois : un prix fixe par mois, le nombre de jours n'entre pas dans le calcul. */
  const tarifMoisRef = choisi?.vehicle?.tarifMoisMinor ?? 0;
  const tarifMoisApplique = tarifMois === '' ? tarifMoisRef : centimes(Number(tarifMois));
  const totalLocation = auMois ? tarifMoisApplique * nbMois : tarifApplique * jours;
  /*
   * La remise porte sur le TOTAL, pas sur le tarif du jour.
   * « 7 500 x 10 jours = 75 000, je vous fais 70 000 » : on saisit 5 000, on ne
   * calcule pas 500 de moins par jour. Elle est plafonnee a ce qu'on facture —
   * une location a montant negatif n'existe pas.
   */
  const remiseMinor = Math.min(remise === '' ? 0 : centimes(Number(remise)), totalLocation + totalSupp);
  // Ce que le client donne aujourd'hui, plafonne au total : on n'encaisse pas
  // plus que ce qu'on facture.
  const trancheMinor = Math.min(tranche === '' ? 0 : centimes(Number(tranche)),
    totalLocation + totalSupp - (remise === '' ? 0 : centimes(Number(remise))));
  const totalHt = totalLocation + totalSupp - remiseMinor;
  const tvaMinor = avecTva ? Math.round((totalHt * tauxTva) / 100) : 0;
  const totalGeneral = totalHt + tvaMinor;

  const bloquantsRestants = (verdict?.blocages ?? []).filter((b) => !derogations[b.regle]);
  /*
   * L'article 01 PREVIENT, il ne barre plus la route.
   *
   * Les champs restent en rouge et les réserves restent affichées — l'agent
   * voit ce qui cloche. Mais « Suivant » s'ouvre : au comptoir, avec le client
   * devant soi, une location refusée par l'écran se faisait sur papier, hors
   * du logiciel, sans aucune trace. Le serveur inscrit chaque réserve au
   * dossier et au journal, avec le nom de celui qui est passé outre.
   *
   * `verdict` non nul reste exigé : pas de vérification, pas de passage. Une
   * liste de réserves vide parce que l'appel a échoué est indiscernable d'un
   * client conforme.
   */
  /*
   * Une seule réserve barre encore la route : la LISTE NOIRE.
   * L'article 01 est une règle générale que l'agence peut assumer au comptoir ;
   * la liste noire est une décision qu'elle a prise sur cette personne, après
   * un impayé ou une voiture abîmée. Il faut un gérant pour l'effacer.
   */
  const listeNoireNonLevee = (verdict?.blocages ?? [])
    .some((b) => b.regle === 'liste_noire' && !derogations[b.regle]);
  const peutContinuer = Boolean(client) && Boolean(verdict) && momentFin > momentDebut
    && !listeNoireNonLevee;

  async function creer(statut: 'option' | 'confirmee') {
    if (!choisi || !client) return;
    setErreur(null); setEnvoi(true);
    try {
      const r = await api.post<{ reservation: { _id: string } }>('/reservations', {
        vehicle: choisi.vehicle._id, customer: client._id,
        dateDebut: new Date(momentDebut).toISOString(),
        dateFin: new Date(momentFin).toISOString(),
        tarifNegocieJourMinor: tarifApplique, kmIllimite, reglement, statut,
        ...(auMois ? { modeTarification: 'mois', tarifMoisNegocieMinor: tarifMoisApplique } : {}),
        remiseMinor, motifRemise: motifRemise.trim() || undefined,
        avecTva,
        ...(reglement === 'par_tranche' && echeances.some((e) => e.date && Number(e.montant) > 0)
          ? {
            echeancier: echeances
              .filter((e) => e.date && Number(e.montant) > 0)
              .map((e) => ({ date: e.date, montantMinor: centimes(Number(e.montant)) })),
          }
          : {}),
        ...(reglement === 'par_tranche' && trancheMinor > 0
          ? { premiereTrancheMinor: trancheMinor, premiereTrancheMode: trancheMode }
          : {}),
        cautionPrevueMinor: caution ? centimes(Number(caution)) : 0,
        supplements: lignesSupp.map((l) => ({ code: l.code, quantite: l.quantite })),
        derogations: Object.entries(derogations).map(([regle, motif]) => ({ regle, motif })),
      });
      const idResa = r.reservation._id;

      /*
       * La caution, prise au comptoir.
       *
       * Une decharge porte un numero et se signe : elle a besoin d'un contrat.
       * On l'etablit donc dans la foulee, on enregistre la caution, et la
       * decharge part a l'impression — le client repart avec son papier.
       *
       * Si cette suite echoue, la LOCATION reste : elle est deja creee et
       * numerotee. On le dit, et l'agent reprend la caution depuis le dossier
       * plutot que de ressaisir toute la location.
       */
      const montantCaution = caution ? centimes(Number(caution)) : 0;
      if (montantCaution > 0 && cautionMaintenant) {
        try {
          const ct = await api.post<{ _id: string }>('/contrats', { reservation: idResa });
          const d = await api.post<{ _id: string; dechargeRemiseNumero: string }>('/cautions', {
            contract: ct._id, montantMinor: montantCaution,
            devise: deviseReference(), forme: cautionForme,
            numeroPiece: cautionPiece.trim() || undefined,
          });
          window.open(urlImpression(`/cautions/${d._id}/imprimer/remise`, getToken()), '_blank');
        } catch {
          nav(`/reservations/${idResa}?caution=a_reprendre`);
          return;
        }
      }
      nav(`/reservations/${idResa}`);
    } catch (e) {
      if (e instanceof ApiError) setErreur(e);
    } finally { setEnvoi(false); }
  }

  const X = T.nouvelleReservation;
  const devise = deviseReference();
  const taxe = nomTaxe ?? X.taxeDefaut;
  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{X.titre}</h1>
          <p>{X.etape(etape, X.etapes[etape - 1] ?? '')}</p>
        </div>
      </div>

      {/* ---------- Etape 1 ---------- */}
      {etape === 1 && (
        <Carte titre={X.datesVehicule}>
          {/* Au jour ou au mois. Le choix change ce qu'on demande ensuite :
              une date de retour, ou un nombre de mois. */}
          <div className="actions" style={{ marginBottom: 14 }}>
            <button type="button" className={`btn btn-s ${!auMois ? 'btn-p' : ''}`}
                    onClick={() => setAuMois(false)}>{X.facturerJour}</button>
            <button type="button" className={`btn btn-s ${auMois ? 'btn-p' : ''}`}
                    onClick={() => setAuMois(true)}>{X.facturerMois}</button>
          </div>

          <div className="grille g2">
            <Champ label={X.depart}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="date" value={debut} onChange={(e) => setDebut(e.target.value)} />
                {avecHeures && (
                  <input type="time" style={{ maxWidth: 120 }} value={heureDebut}
                         onChange={(e) => setHeureDebut(e.target.value)} />
                )}
              </div>
            </Champ>
            {auMois ? (
              <Champ label={X.combienMois}
                     aide={X.retourLe(dateFr(finEffective), jours)}>
                <input type="number" min={1} max={24} value={nbMois}
                       onChange={(e) => setNbMois(Math.max(1, Number(e.target.value) || 1))} />
              </Champ>
            ) : (
              <Champ label={X.retour}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="date" min={debut} value={fin} onChange={(e) => setFin(e.target.value)} />
                  {avecHeures && (
                    <input type="time" style={{ maxWidth: 120 }} value={heureFin}
                           onChange={(e) => setHeureFin(e.target.value)} />
                  )}
                </div>
              </Champ>
            )}
          </div>

          {/*
            * Facultatif : la plupart des locations partent et rentrent le matin.
            * Quand les heures servent, tout suit — la recherche de voiture libre,
            * le temps de preparation, le planning et le calcul du retard.
            */}
          <label style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 10 }}>
            <input type="checkbox" checked={avecHeures}
                   onChange={(e) => setAvecHeures(e.target.checked)} />
            {X.preciserHeures}
            <span className="mini sourdine">
              {X.sinonHeure(HEURE_DEFAUT)}
            </span>
          </label>

          {avecHeures && horaires && (heureFin < horaires.ouverture || heureFin > horaires.fermeture) && (
            <p className="mini" style={{ marginTop: 8, marginBottom: 0, color: 'var(--attention)', fontWeight: 600 }}>
              {X.article02(horaires.ouverture, horaires.fermeture, heureFin)}
            </p>
          )}

          {avecHeures && momentFin <= momentDebut && (
            <p className="mini" style={{ marginTop: 8, marginBottom: 0, color: 'var(--danger)', fontWeight: 600 }}>
              {X.retourApresDepart}
            </p>
          )}

          <div style={{ height: 14 }} />
          {!dispo ? <Squelette /> : dispo.disponibles.length === 0 ? (
            <Bandeau type="warn" titre={X.rienDeLibre}>{X.changezDates}</Bandeau>
          ) : (
            <div className="enveloppe-tbl">
              <table className="tbl">
                {/* En mode mois, on annonce le PRIX MENSUEL. Afficher le tarif
                    journalier ici ferait choisir une voiture sur un chiffre que
                    la location ne facturera jamais. */}
                <thead><tr><th /><th>{X.thVehicule}</th><th>{X.thImmatriculation}</th>
                  <th className="droite">{auMois ? X.tarifMois : X.tarifJour}</th>
                  <th className="droite">{auMois ? X.totalMois(nbMois) : X.totalJours(jours)}</th>
                </tr></thead>
                <tbody>
                  {dispo.disponibles.map((x) => (
                    <tr key={x.vehicle._id} onClick={() => setVehiculeId(x.vehicle._id)} style={{ cursor: 'pointer' }}>
                      <td><input type="radio" checked={vehiculeId === x.vehicle._id} onChange={() => setVehiculeId(x.vehicle._id)} /></td>
                      <td><b>{x.vehicle.marque} {x.vehicle.modele}</b> <span className="mini sourdine">{x.vehicle.categorie}</span></td>
                      <td className="num">{x.vehicle.immatriculation}</td>
                      {auMois ? (
                        (x.vehicle.tarifMoisMinor ?? 0) > 0 ? (
                          <>
                            <td className="droite montant">{montant(x.vehicle.tarifMoisMinor!)}</td>
                            <td className="droite montant">{montant(x.vehicle.tarifMoisMinor! * nbMois)}</td>
                          </>
                        ) : (
                          <td className="droite mini" colSpan={2} style={{ color: 'var(--danger)' }}>
                            {X.pasTarifMois}
                          </td>
                        )
                      ) : (
                        <>
                          <td className="droite montant">{montant(x.tarifJourMinor)}</td>
                          <td className="droite montant">{montant(x.totalMinor)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            {/* `vehiculeId` peut venir de l'URL et ne correspondre a aucune ligne
                (dates changees depuis). Se fier a l'identifiant menait a une
                etape 3 vide, sans recapitulatif ni bouton Retour. */}
            <button className="btn btn-p btn-l" disabled={!choisi || momentFin <= momentDebut} onClick={() => setEtape(2)}>{T.commun.actions.suivant}</button>
          </div>
        </Carte>
      )}

      {/* ---------- Etape 2 : article 01 ---------- */}
      {etape === 2 && (
        <Carte titre={X.client}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'end' }}>
            <Champ label={X.rechercher}>
              <input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder={X.rechercherExemple} autoFocus />
            </Champ>
            <button className="btn" onClick={() => setNouveauClient(true)}>{X.nouveauClient}</button>
          </div>

          <div style={{ marginTop: 12, display: 'grid', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
            {clients.map((c) => (
              <button key={c._id} className="btn" style={{ justifyContent: 'space-between', height: 'auto', padding: '10px 12px' }}
                      onClick={() => setClient(c)} aria-pressed={client?._id === c._id}>
                <span style={{ textAlign: 'left' }}>
                  <b>{nomClient(c)}</b>
                  <div className="mini sourdine">{c.telephone} · {c.pieceType} {c.pieceNumero}</div>
                </span>
                {c.listeNoire && <Badge statut="retard" libelle={X.listeNoire} />}
              </button>
            ))}
          </div>

          {client && verdictKo && (
            <div style={{ marginTop: 16 }}>
              <Bandeau type="ko" titre={X.verificationImpossible}>
                {X.verificationTexte}
                <div style={{ marginTop: 10 }}>
                  <button className="btn btn-s" onClick={() => setClient({ ...client })}>{T.commun.actions.reessayer}</button>
                </div>
              </Bandeau>
            </div>
          )}

          {client && verdict && (
            <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
              <div className="grille g2">
                <Champ label={X.dateNaissance} ok={verdict.age !== undefined && verdict.age >= 28}
                       erreur={verdict.blocages.some((b) => b.regle === 'age_minimum') ? X.ageMinimum(verdict.age ?? 0) : undefined}
                       aide={verdict.age !== undefined ? X.ans(verdict.age) : undefined}>
                  <input readOnly value={dateFr(client.dateNaissance)} />
                </Champ>
                <Champ label={X.permisDelivreLe} ok={(verdict.ancienneteParmis ?? 0) >= 5}
                       erreur={verdict.blocages.some((b) => b.regle === 'anciennete_permis') ? X.permisMinimum(verdict.ancienneteParmis ?? 0) : undefined}
                       aide={verdict.ancienneteParmis !== undefined ? X.anciennete(verdict.ancienneteParmis) : undefined}>
                  <input readOnly value={dateFr(client.permisDelivreLe)} />
                </Champ>
              </div>

              {verdict.blocages.map((b) => {
                const levee = derogations[b.regle];
                return (
                  <div key={b.regle}>
                    {levee ? (
                      <Bandeau type="warn" titre={X.derogationAccordee(b.message)}>{X.motif(levee)}</Bandeau>
                    ) : (
                      <div className="bandeau ko">
                        <div style={{ flex: 1 }}>
                          <b className="bandeau-t">{b.message}</b>
                          {b.detail}
                          <div style={{ marginTop: 10 }}>
                            {/* Une information absente de la fiche ne se « deroge » pas :
                                elle se saisit. Proposer une derogation ici revenait a
                                offrir de signer un controle qu'on n'a pas pu faire. */}
                            <div className="mini" style={{ marginBottom: 8 }}>
                              {b.regle === 'liste_noire'
                                ? X.listeNoireTexte
                                : b.derogeable
                                  ? X.derogeableTexte
                                  : X.manqueTexte}
                            </div>
                            {!b.derogeable ? (
                              <button className="btn btn-s" onClick={() => setClient(null)}>
                                {X.completerFiche}
                              </button>
                            ) : peut('accorder.derogation') ? (
                              <button className="btn btn-s" onClick={() => { setDemandeDerog(b); setMotifSaisi(''); }}>{X.autoriserMalgreTout}</button>
                            ) : (
                              <button className="btn btn-s" onClick={() => { setDemandeDerog(b); setMotifSaisi(''); }}>{X.prevenirGerant}</button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {verdict.conforme && <Bandeau type="ok" titre={X.article01Ok}>{X.article01OkTexte}</Bandeau>}
            </div>
          )}

          <div className="actions" style={{ marginTop: 16, justifyContent: 'space-between' }}>
            <button className="btn" onClick={() => setEtape(1)}>{T.commun.actions.retour}</button>
            <button className="btn btn-p btn-l" disabled={!peutContinuer} onClick={() => setEtape(3)}>{T.commun.actions.suivant}</button>
          </div>
        </Carte>
      )}

      {/* ---------- Etape 3 ---------- */}
      {etape === 3 && !(choisi && client) && (
        <Carte titre={X.plusDisponible}>
          <p className="mini sourdine">
            {X.plusDisponibleTexte}
          </p>
          <div className="actions" style={{ marginTop: 14 }}>
            <button className="btn btn-p btn-l" onClick={() => setEtape(1)}>{X.revenirChoix}</button>
          </div>
        </Carte>
      )}

      {etape === 3 && choisi && client && (
        <div className="grille g2" style={{ alignItems: 'start' }}>
          <Carte titre={X.prixNegocie}>
            <p className="mini sourdine" style={{ marginBottom: 12 }}>
              {X.prixNegocieTexte}
            </p>
            {auMois ? (
              <div className="grille g2">
                <Champ label={X.tarifRefMois}><input readOnly value={montant(tarifMoisRef)} /></Champ>
                <Champ label={X.prixNegocieMois(devise)}
                       aide={X.moisFoisPrix(nbMois, montant(tarifMoisApplique * nbMois))}
                       erreur={tarifMoisRef === 0 && tarifMois === '' ? X.pasTarifMoisErreur : undefined}>
                  <input type="number" min={0} step={1000} value={tarifMois}
                         placeholder={String(tarifMoisRef / 100)}
                         onChange={(e) => setTarifMois(e.target.value === '' ? '' : Number(e.target.value))} />
                </Champ>
              </div>
            ) : (
            <div className="grille g2">
              <Champ label={X.tarifRefJour}><input readOnly value={montant(tarifRef)} /></Champ>
              <Champ label={X.prixNegocieJour(devise)}
                     aide={ecartPct !== 0 ? X.ecart(`${ecartPct > 0 ? '+' : ''}${ecartPct}`) : X.auTarifRef}>
                <input type="number" min={0} step={100} value={tarifNegocie}
                       placeholder={String(tarifRef / 100)} onChange={(e) => setTarifNegocie(e.target.value === '' ? '' : Number(e.target.value))} />
              </Champ>
            </div>
            )}
            <div className="grille g2" style={{ marginTop: 12 }}>
              <Champ label={X.remiseTotal(devise)}
                     aide={X.joursFoisTarif(jours, montant(tarifApplique), montant(totalLocation + totalSupp))}>
                <input type="number" min={0} step={100} value={remise} placeholder="0"
                       onChange={(e) => setRemise(e.target.value === '' ? '' : Number(e.target.value))} />
              </Champ>
              <Champ label={X.motifRemise} aide={X.motifRemiseAide}>
                <input value={motifRemise} onChange={(e) => setMotifRemise(e.target.value)}
                       placeholder={X.motifRemiseExemple} />
              </Champ>
            </div>
            {remiseMinor > 0 && (
              <p className="mini" style={{ marginTop: 6, color: 'var(--primaire)' }}>
                {montant(totalLocation + totalSupp)} − {montant(remiseMinor)} = <b>{montant(totalGeneral)}</b>
              </p>
            )}

            <label style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 12 }}>
              <input type="checkbox" checked={kmIllimite} onChange={(e) => setKmIllimite(e.target.checked)} />
              {X.kmIllimite}<span className="mini sourdine">{X.kmSinon}</span>
            </label>

            {/*
              * Le compteur, au moment de donner les cles.
              * La question n'est pas comptable : c'est « est-ce que cette voiture
              * peut sortir, ou faut-il la vidanger d'abord ». On ne modifie pas
              * le kilometrage officiel du vehicule — ce releve-la a ses fenetres.
              */}
            {(() => {
              const vid = vidanges.find((x) => x.vehicleId === choisi.vehicle._id);
              const km = kmActuel === '' ? null : Number(kmActuel);
              const reste = vid?.prochainKm != null && km != null ? vid.prochainKm - km : null;
              return (
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--bord)' }}>
                  <Champ label={X.kmCompteur}
                         aide={vid ? X.dernierReleve(vid.km.toLocaleString(X.localeNombre)) : undefined}>
                    <input type="number" min={0} step={100} value={kmActuel}
                           placeholder={vid ? String(vid.km) : ''}
                           onChange={(e) => setKmActuel(e.target.value)} />
                  </Champ>
                  {reste !== null && (
                    <p className="mini" style={{
                      marginTop: 8, marginBottom: 0, fontWeight: 600,
                      color: reste <= 0 ? 'var(--danger)' : reste < 1000 ? 'var(--attention)' : 'var(--succes)',
                    }}>
                      {reste <= 0
                        ? X.vidangeDepassee(Math.abs(reste).toLocaleString(X.localeNombre))
                        : reste < 1000
                          ? X.vidangeProche(reste.toLocaleString(X.localeNombre))
                          : X.vidangeReste(reste.toLocaleString(X.localeNombre))}
                    </p>
                  )}
                  {reste === null && vid?.prochainKm == null && (
                    <p className="mini sourdine" style={{ marginTop: 8, marginBottom: 0 }}>
                      {X.aucuneVidange}
                    </p>
                  )}
                </div>
              );
            })()}

            {/*
              * Payer maintenant, ou partir en devant l'argent.
              * Ce choix ne calcule rien : il dit seulement ce qui a ete convenu, pour
              * qu'une dette accordee se distingue d'un encaissement oublie. Le montant
              * du restera, lui, le solde du contrat.
              */}
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--bord)' }}>
              <div className="micro" style={{ marginBottom: 8 }}>{X.commentPaie}</div>
              <div className="actions">
                <button type="button"
                        className={`btn btn-s ${reglement === 'sur_place' ? 'btn-p' : ''}`}
                        onClick={() => setReglement('sur_place')}>{X.paiementSurPlace}</button>
                <button type="button"
                        className={`btn btn-s ${reglement === 'par_tranche' ? 'btn-p' : ''}`}
                        onClick={() => setReglement('par_tranche')}>{X.paiementTranches}</button>
                <button type="button"
                        className={`btn btn-s ${reglement === 'differe' ? 'btn-d' : ''}`}
                        onClick={() => setReglement('differe')}>{X.paiementDiffere}</button>
              </div>

              {/*
                * Par tranches : on encaisse ce que le client donne maintenant, et le
                * reste part dans « Dettes » comme un accord — pas comme un oubli.
                */}
              {reglement === 'par_tranche' && (
                <div className="grille g2" style={{ marginTop: 12 }}>
                  <Champ label={X.verseMaintenant(devise)} aide={X.totalAPayer(montant(totalGeneral))}>
                    <input type="number" min={0} step={1000} value={tranche} placeholder="0"
                           onChange={(e) => setTranche(e.target.value === '' ? '' : Number(e.target.value))} />
                  </Champ>
                  <Champ label={X.payeEn}>
                    <select value={trancheMode} onChange={(e) => setTrancheMode(e.target.value)}>
                      <option value="especes">{X.especes}</option><option value="carte">{X.carte}</option>
                      <option value="cheque">{X.cheque}</option><option value="virement">{X.virement}</option>
                      <option value="traite">{X.traite}</option>
                    </select>
                  </Champ>
                </div>
              )}

              {reglement === 'par_tranche' && (() => {
                const prevu = echeances.reduce((t, e) => t + (Number(e.montant) > 0 ? centimes(Number(e.montant)) : 0), 0);
                const aEtaler = Math.max(0, totalGeneral - trancheMinor);
                return (
                  <div style={{ marginTop: 12 }}>
                    <div className="micro" style={{ marginBottom: 6 }}>{X.calendrier}</div>
                    {echeances.map((e, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                        <input type="date" value={e.date} style={{ flex: 1 }}
                               onChange={(ev) => setEcheances(echeances.map((x, j) => j === i ? { ...x, date: ev.target.value } : x))} />
                        <input type="number" min={0} step={1000} placeholder={X.montant} value={e.montant} style={{ flex: 1 }}
                               onChange={(ev) => setEcheances(echeances.map((x, j) => j === i ? { ...x, montant: ev.target.value } : x))} />
                        <button className="btn btn-s" onClick={() => setEcheances(echeances.filter((_, j) => j !== i))}>{X.retirer}</button>
                      </div>
                    ))}
                    <button className="btn btn-s" onClick={() => setEcheances([...echeances, { date: '', montant: '' }])}>
                      {X.ajouterEcheance}
                    </button>
                    {echeances.length > 0 && (
                      <div className="mini sourdine" style={{ marginTop: 6 }}>
                        {X.echelonnes(montant(prevu), montant(aEtaler))}
                        {prevu !== aEtaler && prevu > 0 && (
                          <b style={{ color: 'var(--attention)' }}>{X.ecartEcheances(montant(Math.abs(aEtaler - prevu)))}</b>
                        )}
                        {X.datesImprimees}
                      </div>
                    )}
                  </div>
                );
              })()}

              <p className="mini sourdine" style={{ marginTop: 10, marginBottom: 0 }}>
                {reglement === 'sur_place'
                  ? X.surPlaceTexte
                  : reglement === 'par_tranche'
                    ? trancheMinor > 0
                      ? X.tranchesTexte(montant(trancheMinor), montant(Math.max(0, totalGeneral - trancheMinor)))
                      : X.tranchesVide
                    : X.differeTexte}
              </p>

              <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--bord)' }}>
                <div className="micro" style={{ marginBottom: 8 }}>{X.laCaution}</div>
                <div className="grille g2">
                  <Champ label={X.montantDevise(devise)}>
                    <input type="number" min={0} step={1000} value={caution}
                           onChange={(e) => setCaution(e.target.value)} placeholder="0" />
                  </Champ>
                  <Champ label={X.forme}>
                    <select value={cautionForme} disabled={!Number(caution)}
                            onChange={(e) => setCautionForme(e.target.value as typeof cautionForme)}>
                      <option value="especes">{X.especes}</option>
                      <option value="cheque">{X.cheque}</option>
                      <option value="traite">{X.traite}</option>
                      <option value="carte">{X.carte}</option>
                    </select>
                  </Champ>
                </div>
                {Number(caution) > 0 && cautionForme !== 'especes' && (
                  <div style={{ marginTop: 10 }}>
                    <Champ label={X.numeroPiece} aide={X.numeroPieceAide}>
                      <input value={cautionPiece} onChange={(e) => setCautionPiece(e.target.value)} />
                    </Champ>
                  </div>
                )}
                {Number(caution) > 0 && (
                  <>
                    <label style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 12 }}>
                      <input type="checkbox" checked={cautionMaintenant}
                             onChange={(e) => setCautionMaintenant(e.target.checked)} />
                      {X.prendsMaintenant}
                    </label>
                    <p className="mini sourdine" style={{ marginTop: 8, marginBottom: 0 }}>
                      {cautionMaintenant
                        ? X.cautionMaintenantTexte
                        : X.cautionPlusTardTexte}
                    </p>
                  </>
                )}
              </div>
            </div>
          </Carte>

          <Carte titre={X.supplementsDepart}>
            <p className="mini sourdine" style={{ marginBottom: 12 }}>
              {X.supplementsTexte}
            </p>
            {catalogue.length === 0 ? (
              <p className="mini sourdine">
                {X.aucunSupplement}
                <b>{X.chemineSupplements}</b>.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {catalogue.map((x) => (
                  <div key={x.code} style={{ display: 'grid', gridTemplateColumns: '1fr 96px', gap: 10, alignItems: 'center' }}>
                    <div>
                      <div>{x.libelle} <span className="mini sourdine">/ {x.unite}</span></div>
                      {x.prixMinor === 0
                        ? <div className="mini" style={{ color: 'var(--danger)' }}>
                            {X.prixNonSaisi(devise)}
                          </div>
                        : <div className="mini sourdine">{montant(x.prixMinor)}</div>}
                    </div>
                    <input type="number" min={0} inputMode="numeric"
                           value={quantites[x.code] ?? ''}
                           placeholder="0"
                           onChange={(e) => setQuantites({ ...quantites, [x.code]: Math.max(0, Number(e.target.value) || 0) })} />
                  </div>
                ))}
              </div>
            )}
          </Carte>

          <Carte titre={X.recapitulatif}>
            <table className="tbl compact">
              <tbody>
                <tr><td>{X.thVehicule}</td><td className="droite"><b>{choisi.vehicle.marque} {choisi.vehicle.modele}</b><div className="mini sourdine">{choisi.vehicle.immatriculation}</div></td></tr>
                <tr><td>{X.client}</td><td className="droite">{nomClient(client)}</td></tr>
                <tr><td>{X.periode}</td><td className="droite">
                  {/* `finEffective`, pas `fin` : au mois, la date de retour est
                      calculee et `fin` n'a jamais ete touchee. Le recapitulatif
                      annoncait donc au client une date qui n'etait pas la sienne. */}
                  {dateFr(debut)}{avecHeures && X.aHeure(heureDebut)} → {dateFr(finEffective)}{avecHeures && X.aHeure(heureFin)}
                </td></tr>
                <tr>
                  <td>{auMois
                    ? <>{X.moisFois(nbMois, montant(tarifMoisApplique))}<div className="mini sourdine">{X.soitJours(jours)}</div></>
                    : <>{X.joursFois(jours, montant(tarifApplique))}</>}</td>
                  <td className="droite montant">{montant(totalLocation)}</td>
                </tr>
                <tr><td>{X.kilometrage}</td><td className="droite">{kmIllimite ? X.illimite : X.kmParJour}</td></tr>
                {Number(caution) > 0 && (
                  <tr><td>{X.cautionConvenue}</td><td className="droite montant">{montant(centimes(Number(caution)))}</td></tr>
                )}
                <tr><td>{X.paiement}</td><td className="droite">
                  {reglement === 'par_tranche'
                    ? <><b>{X.parTranches}</b>{trancheMinor > 0 && <div className="mini sourdine">{X.versesDettes(montant(trancheMinor), montant(Math.max(0, totalGeneral - trancheMinor)))}</div>}</>
                    : reglement === 'differe'
                    ? <b style={{ color: 'var(--danger)' }}>{X.differeCredit}</b>
                    : X.surPlace}
                </td></tr>
                {lignesSupp.map((l) => (
                  <tr key={l.code}>
                    <td>{l.libelle} <span className="mini sourdine">{l.quantite} × {montant(l.prixMinor)}</span></td>
                    <td className="droite montant">{montant(l.totalMinor)}</td>
                  </tr>
                ))}
                {remiseMinor > 0 && (
                  <tr>
                    <td>{X.remise}{motifRemise.trim() && <span className="mini sourdine"> — {motifRemise.trim()}</span>}</td>
                    <td className="droite montant" style={{ color: 'var(--danger)' }}>− {montant(remiseMinor)}</td>
                  </tr>
                )}
                {tvaMinor > 0 && (
                  <>
                    <tr>
                      <td>{X.totalHt}</td>
                      <td className="droite montant">{montant(totalHt)}</td>
                    </tr>
                    <tr>
                      <td>{X.taxeTaux(taxe, tauxTva)}</td>
                      <td className="droite montant">{montant(tvaMinor)}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>

            {/* La case est a cote du total : c'est la qu'on se pose la question. */}
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12 }}>
              <input type="checkbox" checked={avecTva} onChange={(e) => setAvecTva(e.target.checked)} />
              <span>
                {X.locationAvecTaxe(taxe, tauxTva)}
                <div className="mini sourdine">{X.prixTtc(taxe)}</div>
              </span>
            </label>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--bord)' }}>
              <b>{tvaMinor > 0 ? X.totalTtc : X.total}</b>
              <b className="montant" style={{ fontSize: 22 }}>{montant(totalGeneral)}</b>
            </div>

            {Object.keys(derogations).length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Bandeau type="warn" titre={X.derogationArticle01}>{X.derogationJournal}</Bandeau>
              </div>
            )}
            {erreur && (
              <div style={{ marginTop: 12 }}>
                <Bandeau type="ko" titre={erreur.code === 'CHEVAUCHEMENT' ? X.chevauchement : X.impossibleCreer}>{erreur.erreur}</Bandeau>
              </div>
            )}

            <div className="actions" style={{ marginTop: 14, justifyContent: 'space-between' }}>
              <button className="btn" onClick={() => setEtape(2)}>{T.commun.actions.retour}</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" disabled={envoi} onClick={() => void creer('option')}>{X.poserOption}</button>
                <button className="btn btn-p btn-l" disabled={envoi} onClick={() => void creer('confirmee')}>{T.commun.actions.confirmer}</button>
              </div>
            </div>
          </Carte>
        </div>
      )}

      {nouveauClient && (
        <FormulaireClient onFermer={() => setNouveauClient(false)}
                          onCree={(c, v) => { setNouveauClient(false); setClient(c); setVerdict(v); setRecherche(c.nom); }} />
      )}

      {demandeDerog && (
        <Modale titre={peut('accorder.derogation') ? X.autoriserMalgreTout : X.demanderResponsable} onFermer={() => setDemandeDerog(null)}>
          <Bandeau type="warn" titre={demandeDerog.message}>{demandeDerog.detail}</Bandeau>
          <div style={{ marginTop: 14 }}>
            <Champ label={X.motifObligatoire} aide={X.motifAide}>
              <textarea value={motifSaisi} onChange={(e) => setMotifSaisi(e.target.value)} placeholder={X.motifExemple} />
            </Champ>
          </div>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setDemandeDerog(null)}>{T.commun.actions.annuler}</button>
            {peut('accorder.derogation') ? (
              <button className="btn btn-p" disabled={motifSaisi.trim().length < 3}
                      onClick={() => { setDerogations({ ...derogations, [demandeDerog.regle]: motifSaisi.trim() }); setDemandeDerog(null); }}>
                {X.accorderDerogation}
              </button>
            ) : (
              /*
               * L'ancien texte promettait qu'un gerant « autorise depuis son
               * telephone » — cet ecran n'existe pas : la derogation ne peut
               * etre accordee que sur CE poste. On ne promet donc plus ce qu'on
               * ne sait pas faire, et on laisse une trace reelle dans la file
               * « À valider » du gerant, pour qu'il sache qu'un client attend.
               */
              <button className="btn btn-p" disabled={motifSaisi.trim().length < 3 || envoiPrevenir || gerantPrevenu}
                      onClick={async () => {
                        if (!client) return;
                        setEnvoiPrevenir(true);
                        try {
                          await api.post('/corrections', {
                            entite: 'Client', entiteId: client._id,
                            libelleEntite: nomClient(client),
                            champ: X.champDerogation(demandeDerog.message),
                            valeurActuelle: demandeDerog.detail,
                            valeurDemandee: X.autoriserLocation,
                            motif: motifSaisi.trim(), natureFinanciere: false,
                          });
                          setGerantPrevenu(true);
                        } catch { /* le bandeau ci-dessous reste affiche */ }
                        finally { setEnvoiPrevenir(false); }
                      }}>
                {gerantPrevenu ? X.gerantPrevenu : envoiPrevenir ? X.envoi : X.prevenirGerant}
              </button>
            )}
          </div>
          {!peut('accorder.derogation') && (
            <div style={{ marginTop: 12 }}>
              {gerantPrevenu ? (
                <Bandeau type="ok" titre={X.demandePartie}>
                  {X.demandeA}<b>{X.demandeB}</b>{X.demandeC}<b>{X.demandeD}</b>{X.demandeE}
                </Bandeau>
              ) : (
                <p className="mini sourdine">
                  {X.pasLever}
                </p>
              )}
            </div>
          )}
        </Modale>
      )}
    </>
  );
}
